/**
 * StatsImportModal
 *
 * Two jobs remain here:
 *   - The onboarding "Upload prior season stats" import (mode="priorBaseline"):
 *     last season's GameChanger totals as a separate baseline.
 *   - The read-only box score for a game (initialStep="boxscore"), whose
 *     "Update Stats" button hands off to the GameChanger import screen.
 *
 * Per-game imports (scorebook PDF, game stats PDF, stats CSV) all go through
 * components/Import/GameDataImport.tsx and lib/import/gc-save.ts. The game-context
 * save code below predates that and is no longer reachable from the UI: with a
 * game, this modal only ever opens as the viewer. Don't wire a per-game upload
 * back in here; it would recreate the two-screens problem GameDataImport fixed.
 */

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'motion/react';
import { doc, setDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { track } from '../lib/analytics';
import { cn } from '../lib/utils';
import {
  X, FileUp, Check, AlertTriangle, ChevronDown, ArrowRight,
  TrendingUp, TrendingDown, Minus, Upload, RefreshCw, Users
} from 'lucide-react';
import {
  calculateXOVR,
  matchPlayers,
  PlayerMatchResult,
  PlayerStats,
  MatchConfidence,
} from '../lib/xovr-engine';
import { PITCH_COUNT_RULESETS, calculateRestDays } from '../lib/pitch-count-rules';
import { parseGCCsv } from '../lib/gcCsvParser';
import { fieldTimeRecordFor, fieldTimeSourceOf, gameLengthInnings } from '../lib/gc-innings';
import { XOVRLabel } from './XOVRLabel';

// Re-export calculateOVR from utils — the engine needs it to get scoutingOVR
// import { calculateOVR } from '../lib/utils';
// (If calculateOVR is not exported, use player.ovr as the scouting baseline
//  and only update it if ovrBreakdown already exists with a pure scoutingOVR)

// Strips undefined values from an object so Firestore doesn't reject the write.
const stripUndefined = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

// Aggregates per-game stat objects into a single season-level PlayerStats object.
// This is the same weighted-average logic used in StatsView and App.tsx playerSeasonStats.
function aggregatePlayerStats(series: any[]): PlayerStats {
  let pa = 0, ab = 0, h = 0, bb = 0, so = 0, rbi = 0, r = 0, sb = 0, xbh = 0;
  let sumOps = 0, opsWeight = 0;
  let sumAvg = 0, avgWeight = 0;
  let sumObp = 0, obpWeight = 0;
  let ip = 0, pitches = 0, soAllowed = 0, bbAllowed = 0;
  let sumWhip = 0, whipWeight = 0;
  let sumEra  = 0, eraWeight  = 0;

  series.forEach((s: any) => {
    if ((s.pa || 0) > 0 || (s.ab || 0) > 0) {
      pa += (s.pa || 0); ab += (s.ab || 0); h += (s.h || 0);
      bb += (s.bb || 0); so += (s.so || 0); rbi += (s.rbi || 0);
      r  += (s.r  || 0); sb += (s.sb || 0); xbh += (s.xbh || 0);
      if (s.ops != null) { sumOps += s.ops * (s.pa || 1); opsWeight += (s.pa || 1); }
      if (s.avg != null) { sumAvg += s.avg * (s.ab || 1); avgWeight += (s.ab || 1); }
      if (s.obp != null) { sumObp += s.obp * (s.pa || 1); obpWeight += (s.pa || 1); }
    }
    if ((s.ip || 0) > 0) {
      ip += (s.ip || 0); pitches += (s.pitches || 0);
      soAllowed += (s.soAllowed || 0); bbAllowed += (s.bbAllowed || 0);
      if (s.whip != null) { sumWhip += s.whip * (s.ip || 1); whipWeight += (s.ip || 1); }
      if (s.era  != null) { sumEra  += s.era  * (s.ip || 1); eraWeight  += (s.ip || 1); }
    }
  });

  return {
    pa, ab, h, bb, so, rbi, r, sb, xbh,
    ops:  opsWeight  > 0 ? sumOps  / opsWeight  : null,
    avg:  avgWeight  > 0 ? sumAvg  / avgWeight  : null,
    obp:  obpWeight  > 0 ? sumObp  / obpWeight  : null,
    ip, pitches, soAllowed, bbAllowed,
    whip: whipWeight > 0 ? sumWhip / whipWeight : null,
    era:  eraWeight  > 0 ? sumEra  / eraWeight  : null,
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  game?: any;           // optional — if opened from a completed game
  team?: any;           // optional — needed for pitch count ruleset when resolving conflicts
  teamId: string;
  players: any[];
  // When opened from the onboarding-checklist "Upload prior season stats" entry
  // point, route stats to `player.priorSeasonStats` instead of overwriting the
  // live `player.stats` field. Default is currentSeason for backward compat.
  //
  // 'seasonSnapshot' RETIRED (2026-09-09) — no caller passes it any more.
  //
  // It was the Stats-tab "Import CSV" entry point: a whole-season GameChanger
  // export written to `player.seasonSnapshot`, which the Stats tab then
  // displayed AS the season totals instead of summing per-game stats. That
  // stopped a lump-sum file double-counting, but it was a ONE-WAY DOOR: nothing
  // in the app ever cleared the override (the only `seasonSnapshot: null` is in
  // season-rollover.ts, i.e. starting a new season), so one confirmation click
  // silently replaced a whole season of per-game data until the season ended,
  // and with it the per-game field time that fairness and the Field Time
  // Equalizer depend on. Per-game import is now the only path.
  //
  // The write branch below is kept but unreachable: existing snapshots in
  // Firestore are still READ (buildSeasonStats, xOVR, dev-review), and
  // scripts/clear-season-snapshot.ts is how a coach's stale override gets
  // removed. Delete this mode once no player docs carry a seasonSnapshot.
  mode?: 'currentSeason' | 'priorBaseline' | 'seasonSnapshot';
  /** The box score viewer's "Update Stats" button: opens the GameChanger import
      screen for this game. Without it the button is hidden. */
  onUpdateStats?: () => void;
  /** "Return to Schedule" on the success screen; defaults to onClose. */
  onDone?: () => void;
  /** Force the starting step (e.g. 'upload' for an explicit re-import even
      when game.stats already exists and would default to the viewer). */
  initialStep?: 'upload' | 'boxscore';
}

interface PitchConflict {
  playerId: string;
  playerName: string;
  dugoutCount: number;
  gcCount: number;
}

import { format } from 'date-fns';

type Step = 'upload' | 'review' | 'confirm' | 'boxscore';

interface MatchRow extends PlayerMatchResult {
  /** The resolved player ID after any manual override */
  resolvedPlayerId: string | null;
  resolvedPlayerName: string | null;
  skipped: boolean;
  /** xOVR preview */
  currentOVR: number;
  previewXOVR: number | null;
  ovrDelta: number;
  /** Innings at each defensive position from GC CSV */
  fieldTime: Record<string, number> | null;
}

// ---------------------------------------------------------------------------
// Confidence badge
// ---------------------------------------------------------------------------

const ConfidenceBadge = ({ confidence }: { confidence: MatchConfidence }) => {
  const styles: Record<MatchConfidence, string> = {
    exact:     'bg-canvas text-accent-ink border-hairline',
    name:      'bg-canvas text-accent-ink border-hairline',
    number:    'bg-gold-soft   text-gold-ink   border-hairline',
    fuzzy:     'bg-gold-soft   text-gold-ink   border-hairline',
    unmatched: 'bg-[#FBF0F0]     text-danger     border-hairline',
  };
  const labels: Record<MatchConfidence, string> = {
    exact: 'Exact', name: 'Name', number: '#Match', fuzzy: 'Fuzzy', unmatched: 'No Match',
  };
  return (
    <span className={cn('text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border', styles[confidence])}>
      {labels[confidence]}
    </span>
  );
};

// ---------------------------------------------------------------------------
// OVR delta indicator
// ---------------------------------------------------------------------------

const DeltaChip = ({ delta }: { delta: number }) => {
  if (Math.abs(delta) < 0.05) {
    return (
      <span className="flex items-center space-x-0.5 text-ink-2 text-[10px] font-black">
        <Minus className="w-2.5 h-2.5" />
        <span>±0</span>
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span className="flex items-center space-x-0.5 text-accent-ink text-[10px] font-black">
        <TrendingUp className="w-2.5 h-2.5" />
        <span>+{delta.toFixed(1)}</span>
      </span>
    );
  }
  return (
    <span className="flex items-center space-x-0.5 text-danger text-[10px] font-black">
      <TrendingDown className="w-2.5 h-2.5" />
      <span>{delta.toFixed(1)}</span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const StatsImportModal: React.FC<Props> = ({
  isOpen, onClose, game, team, teamId, players, mode = 'currentSeason', onUpdateStats, onDone, initialStep
}) => {
  const [step, setStep]         = useState<Step>(initialStep ?? (game?.stats && Object.keys(game.stats).length > 0 ? 'boxscore' : 'upload'));
  const [rows, setRows]         = useState<MatchRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [error, setError]       = useState<string | null>(null);
  // 'gc' = accept GameChanger number (default), 'dugout' = keep Dugout number
  const [pitchResolutions, setPitchResolutions] = useState<Record<string, 'gc' | 'dugout'>>({});
  const [inputMode, setInputMode] = useState<'file' | 'paste'>('file');
  const [pasteText, setPasteText] = useState('');

  // TODO: sort batting/pitching rows by game lineup order (batting slot + pitcher
  // entry inning). Blocked on verifying that lineupSlot from the scorebook parser
  // matches GC's batting order — currently doesn't. Flagged for investigation.

  // Derive pitch count conflicts: pitcher appears in both Dugout pitchCounts and CSV with different values
  const pitchConflicts: PitchConflict[] = React.useMemo(() => {
    if (!game?.pitchCounts) return [];
    return rows
      .filter(r => !r.skipped && r.resolvedPlayerId && (r.stats?.pitches ?? 0) > 0)
      .flatMap(r => {
        const dugoutCount = game.pitchCounts[r.resolvedPlayerId!];
        const gcCount = r.stats!.pitches!;
        if (!dugoutCount || dugoutCount === gcCount) return [];
        return [{ playerId: r.resolvedPlayerId!, playerName: r.resolvedPlayerName!, dugoutCount, gcCount }];
      });
  }, [rows, game?.pitchCounts]);

  // If game has stats, pre-populate rows or just show the summary in 'confirm' step
  // Actually, let's add a 'boxscore' step for clarity.

  // ── Shared parse logic ────────────────────────────────────────────────────

  const processRawData = useCallback((raw: any[][]) => {
    try {
      const csvRows = parseGCCsv(raw);

      if (csvRows.length === 0) {
        setError('Could not find player data. Make sure this is a GameChanger stats export.');
        return;
      }

      const rosterPlayers = players.map(p => ({ id: p.id, name: p.name, number: p.number }));
      const matches = matchPlayers(csvRows, rosterPlayers);

      const enriched: MatchRow[] = matches.map((m, idx) => {
        const player = players.find(p => p.id === m.matchedPlayerId);
        const scoutingOVR = player?.ovrBreakdown?.scoutingOVR ?? player?.ovr ?? 0;
        const previewBreakdown = m.stats && player
          ? calculateXOVR(scoutingOVR, m.stats)
          : null;

        return {
          ...m,
          resolvedPlayerId:   m.matchedPlayerId,
          resolvedPlayerName: m.matchedPlayerName,
          skipped: m.confidence === 'unmatched',
          currentOVR:   player?.ovr ?? 0,
          previewXOVR:  previewBreakdown?.xovr ?? null,
          ovrDelta:     previewBreakdown?.statDelta ?? 0,
          fieldTime:    csvRows[idx]?.fieldTime ?? null,
        };
      });

      setRows(enriched);
      setStep('review');
    } catch (err) {
      setError('Failed to parse this data. Make sure it\'s exported from GameChanger.');
      console.error(err);
    }
  }, [players]);

  // ── File drop ──────────────────────────────────────────────────────────────

  const onDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    setError(null);

    Papa.parse(file, {
      complete: (result) => processRawData(result.data as any[][]),
      error: () => setError('Could not read this file.'),
      skipEmptyLines: false,
      header: false,
    });
  }, [processRawData]);

  // ── Paste import ──────────────────────────────────────────────────────────

  const handlePasteImport = useCallback(() => {
    if (!pasteText.trim()) return;
    setError(null);

    Papa.parse(pasteText, {
      complete: (result) => processRawData(result.data as any[][]),
      error: () => setError('Could not parse the pasted text.'),
      skipEmptyLines: false,
      header: false,
    });
  }, [pasteText, processRawData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'text/plain': ['.csv'] },
    maxFiles: 1,
  });

  // ── Manual player override ─────────────────────────────────────────────────

  const overridePlayer = (csvIndex: number, playerId: string) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    const scoutingOVR = player?.ovrBreakdown?.scoutingOVR ?? player?.ovr ?? 0;
    const row = rows[csvIndex];
    const preview = row.stats ? calculateXOVR(scoutingOVR, row.stats) : null;

    setRows(prev => prev.map((r, i) => i !== csvIndex ? r : {
      ...r,
      resolvedPlayerId:   player.id,
      resolvedPlayerName: player.name,
      confidence: 'exact' as MatchConfidence,
      skipped: false,
      previewXOVR: preview?.xovr ?? null,
      ovrDelta:    preview?.statDelta ?? 0,
    }));
  };

  const toggleSkip = (csvIndex: number) => {
    setRows(prev => prev.map((r, i) => i !== csvIndex ? r : { ...r, skipped: !r.skipped }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    setIsSaving(true);
    let count = 0;

    try {
      // 1. If we have a game context, save stats to the game document
      if (game?.id) {
        const gameStats: Record<string, any> = {};
        rows.forEach(row => {
          if (!row.skipped && row.resolvedPlayerId && row.stats) {
            gameStats[row.resolvedPlayerId] = stripUndefined(row.stats as any);
          }
        });

        await setDoc(
          doc(db, `teams/${teamId}/games`, game.id),
          { stats: gameStats },
          { merge: true }
        );

        // 1b. Write GC field time. A GameChanger import ALWAYS supersedes the
        // live game engine's inningLogs.
        //
        // This used to skip the write whenever inningLogs had any entry at all.
        // Live logs are saved one inning at a time (each End Inning tap), so a
        // coach who starts the live engine and then stops to coach leaves a
        // one-inning stub behind, and that stub permanently blocked the real
        // GameChanger field time from ever being imported for that game. The
        // import looked successful (stats were written) while gcFieldTime was
        // silently dropped, so the fairness matrix and the Field Time Report
        // showed players as absent from games they had played.
        //
        // GC is the system of record for a finished game: it counts every
        // defensive inning, where a live log only holds what the coach had time
        // to tap in. The import is always the more complete record, so it wins
        // unconditionally.
        {
          const gcFieldTime: Record<string, Record<string, number>> = {};
          rows.forEach(row => {
            if (!row.skipped && row.resolvedPlayerId && row.fieldTime) {
              gcFieldTime[row.resolvedPlayerId] = row.fieldTime;
            }
          });
          if (Object.keys(gcFieldTime).length > 0) {
            await setDoc(
              doc(db, `teams/${teamId}/games`, game.id),
              { gcFieldTime },
              { merge: true }
            );
          }
        }

        // 1c. Apply pitch count conflict resolutions
        for (const conflict of pitchConflicts) {
          const resolution = pitchResolutions[conflict.playerId] ?? 'gc';
          if (resolution !== 'gc') continue;

          const updatedPitchCounts = { ...(game.pitchCounts || {}), [conflict.playerId]: conflict.gcCount };
          await setDoc(doc(db, `teams/${teamId}/games`, game.id), { pitchCounts: updatedPitchCounts }, { merge: true });

          if (team) {
            const ruleset = PITCH_COUNT_RULESETS[team.settings?.pitchCountRuleset || 'little_league'];
            const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
            const ageRules = (ruleset.id === 'custom' && team.settings?.customRules)
              ? team.settings.customRules
              : (ruleset.ageGroups[ageGroup] || ruleset.ageGroups['12U'] || { dailyMax: 85, restThresholds: [] });
            const restDays = calculateRestDays(conflict.gcCount, ageRules.restThresholds || []);
            const gameDate = new Date(game.date);
            const eligibleDate = new Date(gameDate);
            eligibleDate.setDate(eligibleDate.getDate() + restDays + 1);
            eligibleDate.setHours(0, 0, 0, 0);

            const player = players.find(p => p.id === conflict.playerId);
            if (player) {
              const filteredLog = (player.pitchingLog || []).filter((e: any) => e.gameId !== game.id);
              await setDoc(
                doc(db, `teams/${teamId}/players`, conflict.playerId),
                {
                  pitchingLog: [...filteredLog, {
                    date: gameDate.toISOString(),
                    pitchCount: conflict.gcCount,
                    requiredRestDays: restDays,
                    eligibleDate: eligibleDate.toISOString(),
                    gameId: game.id,
                    tournamentId: game.tournamentId || null,
                    opponent: game.opponent || 'Unknown',
                  }]
                },
                { merge: true }
              );
            }
          }
        }
      }

      // 1b. Write GC field time. Same rule as above: the import always supersedes
      // whatever the live game engine recorded.
      if (game?.id) {
        const gcFieldTime: Record<string, Record<string, number>> = {};
        
        rows.forEach(row => {
          if (!row.skipped && row.resolvedPlayerId && row.fieldTime) {
            gcFieldTime[row.resolvedPlayerId] = row.fieldTime;
          }
        });

        if (Object.keys(gcFieldTime).length > 0) {
          // Determine game innings to calculate bench time
          let gameInnings = game.innings || 0;
          if (gameInnings === 0) {
            // Check max IP
            rows.forEach(row => {
              if (!row.skipped && row.stats && row.stats.ip && row.stats.ip > gameInnings) {
                gameInnings = Math.ceil(row.stats.ip);
              }
            });
          }
          if (gameInnings === 0) gameInnings = 6; // default fallback

          await setDoc(
            doc(db, `teams/${teamId}/games`, game.id),
            { gcFieldTime, inferredInnings: gameInnings },
            { merge: true }
          );

          // We also need to update the player.benchLog and player.seasonBenchInnings
          // so that the Lineup Engine (which reads from player docs, not game docs) knows about this bench time.
          // Except on a team using "Dugout live games" with a whole-game live log
          // for this game: that log stays the record, and its End Game bench
          // counts stand (support log G9). Same rule as gc-save, and the same
          // game length every screen uses once this import is saved.
          const afterImport = { ...game, gcFieldTime, inferredInnings: gameInnings };
          const gameLength = gameLengthInnings(afterImport, team?.defaultInnings);
          const dugoutKeepsBench =
            fieldTimeRecordFor(afterImport, fieldTimeSourceOf(team), team?.defaultInnings) === 'live';
          if (!dugoutKeepsBench) {
            for (const row of rows) {
              if (row.skipped || !row.resolvedPlayerId || !row.fieldTime) continue;

              let totalInningsInField = 0;
              Object.values(row.fieldTime).forEach(val => totalInningsInField += val);

              const benchInnings = Math.max(0, gameLength - totalInningsInField);
            
              const player = players.find(p => p.id === row.resolvedPlayerId);
              if (player) {
                const priorBenchLog = player.benchLog && typeof player.benchLog === 'object' ? player.benchLog : {};
                const newBenchLog = { ...priorBenchLog, [game.id]: benchInnings };
                const seasonBenchInnings = Object.values(newBenchLog).reduce((sum: number, n) => sum + (n as number), 0);

                await setDoc(
                  doc(db, `teams/${teamId}/players`, row.resolvedPlayerId),
                  {
                    benchLog: newBenchLog,
                    seasonBenchInnings
                  },
                  { merge: true }
                );
              }
            }
          }
        }
      }

      // 2. Update players with xOVR based on full season stats.
      // When importing a per-game CSV (game context), a single game has very low
      // confidence (3-4 PA → weight 0.15), so xOVR barely moves from scoutingOVR.
      // Fix: fetch all game docs and aggregate the full season sample first.
      let playerGameSeries: Record<string, any[]> | null = null;
      if (game?.id) {
        const allGamesSnap = await getDocs(collection(db, `teams/${teamId}/games`));
        playerGameSeries = {};
        allGamesSnap.docs.forEach(gDoc => {
          const gStats = gDoc.data().stats;
          if (!gStats) return;
          Object.entries(gStats).forEach(([pid, s]) => {
            playerGameSeries![pid] = playerGameSeries![pid] || [];
            playerGameSeries![pid].push(s);
          });
        });
      }

      const xovrSnapshots: Record<string, number> = {};

      for (const row of rows) {
        if (row.skipped || !row.resolvedPlayerId || !row.stats) continue;

        const player = players.find(p => p.id === row.resolvedPlayerId);
        if (!player) continue;

        // Season import (no game): use CSV stats directly (they're already season-level).
        // Game import: use full-season aggregated stats so confidence scales correctly.
        const statsForXOVR: PlayerStats = playerGameSeries
          ? aggregatePlayerStats(playerGameSeries[row.resolvedPlayerId] || [row.stats])
          : row.stats;

        const scoutingOVR = player?.ovrBreakdown?.scoutingOVR ?? player?.ovr ?? 0;
        const breakdown   = calculateXOVR(scoutingOVR, statsForXOVR);

        if (mode === 'priorBaseline') {
          // Prior-baseline writes: store under priorSeasonStats so the live
          // `stats` field stays clean. Don't touch ovr/ovrBreakdown — those
          // belong to the current season. xOVR's confidence weighting will
          // pick up the baseline separately.
          const priorYear = team?.seasonYear ? (team.seasonYear - 1) : (new Date().getFullYear() - 1);
          const priorSeason = team?.season ?? 'Prior';
          await setDoc(
            doc(db, `teams/${teamId}/players`, row.resolvedPlayerId),
            {
              priorSeasonStats: {
                season: priorSeason,
                year: priorYear,
                stats: stripUndefined(statsForXOVR as any),
                ...(row.fieldTime ? { fieldTime: row.fieldTime } : {}),
              },
            },
            { merge: true }
          );
        } else if (mode === 'seasonSnapshot') {
          // Stats-tab whole-season import: store the GameChanger season totals as
          // an authoritative `seasonSnapshot`. The Stats tab displays these AS the
          // season totals (instead of summing games), so a lump-sum season file
          // can't double-count on top of game-by-game data. Per-game `game.stats`
          // are never touched here — the import is non-destructive. xOVR/ovr are
          // updated from the season sample (the formula itself is unchanged).
          await setDoc(
            doc(db, `teams/${teamId}/players`, row.resolvedPlayerId),
            {
              seasonSnapshot: {
                stats: stripUndefined(statsForXOVR as any),
                ...(row.fieldTime ? { fieldTime: row.fieldTime } : {}),
                importedAt: new Date().toISOString(),
                source: 'gcSeasonImport',
              },
              ovr:          breakdown.xovr,
              ovrBreakdown: breakdown,
            },
            { merge: true }
          );
        } else {
          await setDoc(
            doc(db, `teams/${teamId}/players`, row.resolvedPlayerId),
            {
              stats:        stripUndefined(statsForXOVR as any),
              ovr:          breakdown.xovr,
              ovrBreakdown: breakdown,
            },
            { merge: true }
          );

          // Freeze xOVR at this point in time so the trendline stays historically accurate
          // even if the coach re-scouts the player later.
          if (game?.id) xovrSnapshots[row.resolvedPlayerId] = breakdown.xovr;
        }

        count++;
      }

      // Write frozen xOVR snapshots back to the game document (one merge write)
      if (game?.id && Object.keys(xovrSnapshots).length > 0) {
        const snapshotUpdate: Record<string, { xovrSnapshot: number }> = {};
        Object.entries(xovrSnapshots).forEach(([pid, xovr]) => {
          snapshotUpdate[pid] = { xovrSnapshot: xovr };
        });
        await setDoc(
          doc(db, `teams/${teamId}/games`, game.id),
          { stats: snapshotUpdate },
          { merge: true }
        );
      }

      // Import-success marker on the team doc. Deliberately NOT the same signal
      // as settings.onboardingChecklist.statsUploaded, which App.tsx flips on
      // modal *close* — that's true even when nothing was imported. This only
      // lands when players were actually written, so server-side triggers
      // (lifecycle email, Launch Team grant) can trust it as real success.
      if (count > 0) {
        await setDoc(
          doc(db, 'teams', teamId),
          {
            lastStatsImport: {
              at: new Date().toISOString(),
              mode,
              playersMatched: count,
              // Season-level imports are the ones that light up the stats
              // engine (Smart Scout, xOVR, streaks); per-game is a single game.
              isSeasonLevel: mode === 'priorBaseline' || mode === 'seasonSnapshot',
              ...(game?.id ? { gameId: game.id } : {}),
            },
          },
          { merge: true }
        );
      }

      setSavedCount(count);
      setStep('confirm');
      // Only on real success (count > 0 wrote lastStatsImport above), so this
      // matches the `stats_imported` event the Cloud Function fires off that
      // same field and the rollup's withImportedStats count.
      if (count > 0) {
        track('stats_imported', {
          mode,
          playersMatched: count,
          isSeasonLevel: mode === 'priorBaseline' || mode === 'seasonSnapshot',
        });
      }
    } catch (err) {
      setError('Something went wrong saving stats. Check your connection and try again.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const matched    = rows.filter(r => !r.skipped && r.resolvedPlayerId);
  const needsReview = rows.filter(r => r.confidence === 'fuzzy' || r.confidence === 'number');
  const unmatched  = rows.filter(r => r.confidence === 'unmatched' && !r.skipped);
  const displayRows = rows; // lineup-order sort pending lineupSlot investigation

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 bg-surface flex flex-col"
    >
      {/* Header */}
      <header className="safe-top p-4 sm:p-6 border-b border-hairline flex items-center justify-between sticky top-0 bg-surface backdrop-blur-xl z-10">
        <div>
          {step === 'boxscore' && game ? (
            <>
              <h2 className="text-xl font-black italic uppercase tracking-tight">
                {game.isHome ? 'vs ' : '@ '}{game.opponent}
              </h2>
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-0.5">
                Box Score · {format(new Date(game.date), 'MMM d, yyyy')}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-black italic uppercase tracking-tight">{mode === 'seasonSnapshot' ? 'Import Season Stats' : 'Import Stats'}</h2>
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-0.5">
                GameChanger CSV · <XOVRLabel /> Update
              </p>
            </>
          )}
        </div>
        <button onClick={onClose} className="p-2 bg-canvas rounded-lg text-ink-2 hover:text-ink transition-colors">
          <X className="w-5 h-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">

        {/* ── Step 1: Upload ── */}
        <AnimatePresence mode="wait">
          {step === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="p-6 max-w-md mx-auto space-y-6"
            >
              <div className="space-y-2">
                <p className="text-sm text-ink-2 leading-relaxed">
                  Import your GameChanger stats to update your roster and recalculate <XOVRLabel /> automatically.
                </p>
                <p className="text-[10px] text-ink-2 leading-relaxed pt-1 border-t border-hairline">
                  Always import your most recent GC export. Each CSV already contains your full season totals, so the latest file is all you need.
                </p>
              </div>

              {/* Mode toggle */}
              <div className="flex bg-canvas rounded-2xl p-1 gap-1">
                <button
                  onClick={() => setInputMode('file')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all',
                    inputMode === 'file'
                      ? 'bg-raised text-ink'
                      : 'text-ink-2 hover:text-ink-2'
                  )}
                >
                  <FileUp className="w-3.5 h-3.5" />
                  Upload File
                </button>
                <button
                  onClick={() => setInputMode('paste')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all',
                    inputMode === 'paste'
                      ? 'bg-raised text-ink'
                      : 'text-ink-2 hover:text-ink-2'
                  )}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Paste Text
                </button>
              </div>

              {inputMode === 'file' ? (
                <>
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest -mt-2">
                    In GameChanger: Stats → Season → Export → CSV
                  </p>
                  <div
                    {...getRootProps()}
                    className={cn(
                      'border-2 border-dashed rounded-3xl p-12 flex flex-col items-center justify-center space-y-4 cursor-pointer transition-all',
                      isDragActive
                        ? 'border-ink/60 bg-canvas'
                        : 'border-hairline hover:border-ink-mute hover:bg-canvas'
                    )}
                  >
                    <input {...getInputProps()} />
                    <div className="w-16 h-16 bg-canvas rounded-2xl flex items-center justify-center">
                      <FileUp className="w-8 h-8 text-ink-2" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-sm font-bold text-ink">
                        {isDragActive ? 'Drop it' : 'Drop your CSV here'}
                      </p>
                      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                        or tap to browse
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest -mt-2">
                    In GameChanger: Stats → Season → Export → Copy to Clipboard
                  </p>
                  <textarea
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder="Paste your GameChanger stats here..."
                    className="w-full h-40 bg-canvas border border-hairline rounded-2xl p-4 text-xs text-ink-2 placeholder-ink-2 resize-none focus:outline-none focus:border-ink-mute"
                  />
                  <button
                    onClick={handlePasteImport}
                    disabled={!pasteText.trim()}
                    className={cn(
                      'w-full py-3 rounded-2xl text-sm font-black uppercase tracking-widest transition-all',
                      pasteText.trim()
                        ? 'bg-ink text-surface hover:bg-ink'
                        : 'bg-raised text-ink-2 cursor-not-allowed'
                    )}
                  >
                    Import Stats
                  </button>
                </>
              )}

              {error && (
                <div className="flex items-start space-x-3 p-4 bg-[#FBF0F0] border border-hairline rounded-2xl">
                  <AlertTriangle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
                  <p className="text-sm text-danger">{error}</p>
                </div>
              )}
            </motion.div>
          )}

          {/* ── Step 2: Review matches ── */}
          {step === 'review' && (
            <motion.div
              key="review"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="p-6 space-y-6 max-w-2xl mx-auto"
            >
              {/* Summary bar */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Matched', value: matched.length, color: 'text-accent-ink' },
                  { label: 'Review needed', value: needsReview.length, color: 'text-gold-ink' },
                  { label: 'Unmatched', value: unmatched.length, color: 'text-danger' },
                ].map(s => (
                  <div key={s.label} className="bg-canvas rounded-2xl border border-hairline p-3 text-center">
                    <p className={cn('text-2xl font-black italic', s.color)}>{s.value}</p>
                    <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Pitch count conflict resolution */}
              {pitchConflicts.length > 0 && (
                <div className="bg-gold-soft border border-hairline rounded-2xl p-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-gold-ink shrink-0" />
                    <p className="text-xs font-semibold text-gold-ink uppercase tracking-widest">
                      Pitch Count Discrepancy
                    </p>
                  </div>
                  <p className="text-[11px] text-ink-2 leading-relaxed">
                    Dugout tracked different pitch counts than GameChanger for the pitchers below. Choose which number to use. GC is recommended unless you manually corrected counts in the dugout.
                  </p>
                  <div className="space-y-3">
                    {pitchConflicts.map(conflict => {
                      const resolution = pitchResolutions[conflict.playerId] ?? 'gc';
                      return (
                        <div key={conflict.playerId} className="bg-surface rounded-xl p-3 space-y-2">
                          <p className="text-sm font-black uppercase">{conflict.playerName}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => setPitchResolutions(prev => ({ ...prev, [conflict.playerId]: 'gc' }))}
                              className={cn(
                                'py-2.5 px-3 rounded-xl text-left transition-all',
                                resolution === 'gc'
                                  ? 'bg-ink text-surface'
                                  : 'bg-canvas border border-hairline text-ink-2 hover:border-ink-mute'
                              )}
                            >
                              <p className="text-[9px] font-semibold uppercase tracking-widest mb-0.5">
                                {resolution === 'gc' ? '✓ Using GameChanger' : 'Use GameChanger'}
                              </p>
                              <p className="text-lg font-black italic">{conflict.gcCount} pitches</p>
                            </button>
                            <button
                              onClick={() => setPitchResolutions(prev => ({ ...prev, [conflict.playerId]: 'dugout' }))}
                              className={cn(
                                'py-2.5 px-3 rounded-xl text-left transition-all',
                                resolution === 'dugout'
                                  ? 'bg-ink-2 text-surface'
                                  : 'bg-canvas border border-hairline text-ink-2 hover:border-ink-mute'
                              )}
                            >
                              <p className="text-[9px] font-semibold uppercase tracking-widest mb-0.5">
                                {resolution === 'dugout' ? '✓ Keeping Dugout' : 'Keep Dugout'}
                              </p>
                              <p className="text-lg font-black italic">{conflict.dugoutCount} pitches</p>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Player rows */}
              <div className="space-y-2">
                {displayRows.map((row) => {
                  // Always mutate via the original rows index so sort order doesn't
                  // corrupt overridePlayer / toggleSkip index lookups.
                  const originalIdx = rows.indexOf(row);
                  const needsAttention = row.confidence === 'fuzzy'
                    || row.confidence === 'number'
                    || row.confidence === 'unmatched';

                  return (
                    <div
                      key={`${row.csvNumber}-${row.csvLast}`}
                      className={cn(
                        'bg-canvas rounded-2xl border p-4 space-y-3 transition-all',
                        row.skipped
                          ? 'opacity-40 border-hairline'
                          : needsAttention
                            ? 'border-hairline'
                            : 'border-hairline'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        {/* CSV name */}
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-raised rounded-lg flex items-center justify-center">
                            <span className="text-[10px] font-black text-ink-2">#{row.csvNumber}</span>
                          </div>
                          <div>
                            <p className="text-sm font-black uppercase">
                              {row.csvFirst} {row.csvLast}
                            </p>
                            {row.stats && (
                              <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">
                                {row.stats.gp ? `${row.stats.gp} GP` : ''}
                                {row.stats.gp && row.stats.pa ? ' · ' : ''}
                                {row.stats.pa ? `${row.stats.pa} PA` : ''}
                                {row.stats.pa && row.stats.ops ? ' · ' : ''}
                                {row.stats.ops ? `OPS ${row.stats.ops.toFixed(3).replace(/^0/, '')}` : ''}
                                {row.stats.ip && row.stats.ip > 0 ? ` · ${row.stats.ip.toFixed(1)} IP` : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <ConfidenceBadge confidence={row.confidence} />
                          <button
                            onClick={() => toggleSkip(originalIdx)}
                            className={cn(
                              'text-[9px] font-black uppercase tracking-widest transition-colors',
                              row.skipped ? 'text-accent-ink' : 'text-ink-2 hover:text-danger'
                            )}
                          >
                            {row.skipped ? 'Include' : 'Skip'}
                          </button>
                        </div>
                      </div>

                      {/* Match target + xOVR preview */}
                      {!row.skipped && (
                        <div className="flex items-center justify-between bg-surface rounded-xl p-3">
                          <div className="flex items-center space-x-2 min-w-0">
                            <ArrowRight className="w-3 h-3 text-ink-2 shrink-0" />
                            {row.confidence === 'unmatched' || row.confidence === 'fuzzy' ? (
                              <select
                                value={row.resolvedPlayerId ?? ''}
                                onChange={e => overridePlayer(originalIdx, e.target.value)}
                                className="bg-canvas border border-ink-mute rounded-lg px-2 py-1 text-[10px] font-bold outline-none text-ink-2 min-w-0 flex-1"
                              >
                                <option value="">Select player</option>
                                {players.map(p => (
                                  <option key={p.id} value={p.id}>#{p.number} {p.name}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-[10px] font-bold text-ink-2 uppercase truncate">
                                {row.resolvedPlayerName}
                              </span>
                            )}
                          </div>

                          {row.previewXOVR !== null && (
                            <div className="flex items-center space-x-2 shrink-0 ml-3">
                              <span className="text-[10px] font-bold text-ink-2">
                                {row.currentOVR.toFixed(1)}
                              </span>
                              <ArrowRight className="w-2.5 h-2.5 text-ink-2" />
                              <span className={cn(
                                'text-[10px] font-black',
                                row.ovrDelta > 0.05 ? 'text-accent-ink'
                                  : row.ovrDelta < -0.05 ? 'text-danger'
                                  : 'text-ink-2'
                              )}>
                                {row.previewXOVR.toFixed(1)}
                              </span>
                              <DeltaChip delta={row.ovrDelta} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {error && (
                <div className="flex items-start space-x-3 p-4 bg-[#FBF0F0] border border-hairline rounded-2xl">
                  <AlertTriangle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
                  <p className="text-sm text-danger">{error}</p>
                </div>
              )}
            </motion.div>
          )}

          {/* ── Step 3: Done ── */}
          {step === 'confirm' && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 max-w-md mx-auto flex flex-col items-center justify-center min-h-[400px] space-y-6 text-center"
            >
              <div className="w-20 h-20 bg-ink rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.3)]">
                <Check className="w-10 h-10 text-surface" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-black italic uppercase">Stats Imported</h3>
                <p className="text-ink-2">
                  <span className="text-ink font-black">{savedCount} players</span> updated.
                  <XOVRLabel /> recalculated across your roster.
                </p>
              </div>
              <div className="flex flex-col space-y-3 w-full">
                <button
                  onClick={() => (onDone ?? onClose)()}
                  className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic"
                >
                  Return to Schedule
                </button>
                <button
                  onClick={() => {
                    setStep('upload');
                    setRows([]);
                    setSavedCount(0);
                    setError(null);
                  }}
                  className="w-full py-3 bg-canvas text-ink-2 font-black rounded-2xl uppercase italic text-sm hover:text-ink transition-colors"
                >
                  Import Another File
                </button>
              </div>
            </motion.div>
          )}

          {/* ── Step 4: Box Score (View only) ── */}
          {step === 'boxscore' && game?.stats && (
            <motion.div
              key="boxscore"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="p-6 space-y-6 max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-black italic uppercase tracking-tight">Box Score</h3>
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
                    {game.isHome ? 'Home vs ' : 'Away @ '}{game.opponent} · {format(new Date(game.date), 'MMM d, yyyy')}
                  </p>
                </div>
                {onUpdateStats && (
                  <button
                    onClick={onUpdateStats}
                    className="px-4 py-2 bg-canvas text-ink-2 text-[10px] font-black uppercase tracking-widest rounded-xl hover:text-ink transition-colors"
                  >
                    Update Stats
                  </button>
                )}
              </div>

              <div className="bg-canvas rounded-3xl border border-hairline overflow-hidden overflow-x-auto">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-surface border-b border-hairline">
                    <tr>
                      <th className="px-4 py-3 font-black uppercase text-ink-2 text-[10px] tracking-widest sticky left-0 bg-surface">Player</th>
                      {['AB', 'R', 'H', 'RBI', 'BB', 'SO', 'AVG', 'OPS'].map(h => (
                        <th key={h} className="px-3 py-3 font-semibold text-[10px] tracking-widest text-center uppercase text-ink-2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {Object.entries(game.stats).map(([pid, s]: [string, any], idx) => {
                      const p = players.find(pl => pl.id === pid);
                      if (!p) return null;
                      return (
                        <tr key={pid} className={idx % 2 === 0 ? 'bg-canvas' : 'bg-canvas'}>
                          <td className="px-4 py-3 sticky left-0 bg-inherit">
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 bg-raised rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                                #{p.number}
                              </div>
                              <span className="font-black text-sm">{p.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.ab ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.r ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.h ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.rbi ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.bb ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink-2">{s.so ?? 0}</td>
                          <td className="px-3 py-3 text-center font-bold text-ink">{(s.avg || 0).toFixed(3).replace(/^0/, '')}</td>
                          <td className="px-3 py-3 text-center font-bold text-accent-ink">{(s.ops || 0).toFixed(3).replace(/^0/, '')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pitching Box */}
              {Object.values(game.stats).some((s: any) => s.ip > 0) && (
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold text-ink-2 uppercase tracking-widest ml-1">Pitching Stats</h4>
                  <div className="bg-canvas rounded-3xl border border-hairline overflow-hidden overflow-x-auto">
                    <table className="w-full text-left text-xs whitespace-nowrap">
                      <thead className="bg-surface border-b border-hairline">
                        <tr>
                          <th className="px-4 py-3 font-black uppercase text-ink-2 text-[10px] tracking-widest sticky left-0 bg-surface">Pitcher</th>
                          {['IP', 'H', 'ER', 'BB', 'SO', 'S%', 'FPS%', 'P/BF', 'ERA', 'WHIP'].map(h => (
                            <th key={h} className="px-3 py-3 font-semibold text-[10px] tracking-widest text-center uppercase text-ink-2">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-hairline">
                        {(() => {
                          const normalizeRate = (val: any) => {
                            if (val == null) return null;
                            const n = parseFloat(val);
                            if (isNaN(n)) return null;
                            return n > 1 ? n / 100 : n;
                          };
                          
                          return Object.entries(game.stats)
                            .filter(([_, s]: [any, any]) => s.ip > 0 || s.pitches > 0)
                            .map(([pid, s]: [string, any], idx) => {
                              const p = players.find(pl => pl.id === pid);
                              if (!p) return null;
                              const sPct = normalizeRate(s.sPct);
                              const fpsPct = normalizeRate(s.fpsPct);

                              return (
                                <tr key={pid} className={idx % 2 === 0 ? 'bg-canvas' : 'bg-canvas'}>
                                  <td className="px-4 py-3 sticky left-0 bg-inherit">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-7 h-7 bg-raised rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                                        #{p.number}
                                      </div>
                                      <span className="font-black text-sm">{p.name}</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{(s.ip || 0).toFixed(1)}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{s.hAllowed ?? s.h ?? 0}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{s.erAllowed ?? 0}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{s.bbAllowed ?? s.bb ?? 0}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{s.soAllowed ?? s.so ?? 0}</td>
                                  <td className={cn(
                                    "px-3 py-3 text-center font-black",
                                    (sPct || 0) >= 0.6 ? "text-accent-ink" : (sPct || 0) < 0.5 ? "text-danger" : "text-ink-2"
                                  )}>
                                    {sPct != null ? `${Math.round(sPct * 100)}%` : '—'}
                                  </td>
                                  <td className={cn(
                                    "px-3 py-3 text-center font-black",
                                    (fpsPct || 0) >= 0.6 ? "text-accent-ink" : (fpsPct || 0) < 0.4 ? "text-danger" : "text-ink-2"
                                  )}>
                                    {fpsPct != null ? `${Math.round(fpsPct * 100)}%` : '—'}
                                  </td>
                                  <td className="px-3 py-3 text-center font-bold text-ink-2">{(s.p_bf || 0).toFixed(1)}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink">{(s.era || 0).toFixed(2)}</td>
                                  <td className="px-3 py-3 text-center font-bold text-ink">{(s.whip || 0).toFixed(2)}</td>
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer CTA */}
      {step === 'review' && (
        <div className="p-6 border-t border-hairline bg-surface space-y-2">
          <div className="flex items-center justify-between text-[10px] font-semibold text-ink-2 uppercase tracking-widest px-1 mb-3">
            <span>{matched.length} players will be updated</span>
            {rows.filter(r => r.skipped).length > 0 && (
              <span>{rows.filter(r => r.skipped).length} skipped</span>
            )}
          </div>
          <button
            onClick={handleConfirm}
            disabled={isSaving || matched.length === 0}
            className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(16,185,129,0.2)]"
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Apply Stats & Update <XOVRLabel /></span>
              </>
            )}
          </button>
        </div>
      )}
    </motion.div>
  );
};
